import React, { Suspense, lazy, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router';
import ErrorBoundary from '@util/Errors/ErrorBoundary';
import Layout from '@components/layout';
import Providers from '@util/providers';
import AuthProvider from '@util/providers/AuthProvider';
import { LocaleProvider } from '@util/providers/Locale';
import { setNavigator } from '@util/appNavigate';

const IndexPage = lazy(() => import('./pages/index'));
const NotFoundPage = lazy(() => import('./pages/404'));
const AccountPage = lazy(() => import('./pages/account'));
const BoardmakerLicensePage = lazy(() => import('./pages/boardmakerLicense'));
const CommunityIndexPage = lazy(() => import('./pages/community/index'));
const CommunityStartPage = lazy(() => import('./pages/community/start'));
const ComparePage = lazy(() => import('./pages/compare'));
const DownloadsPage = lazy(() => import('./pages/downloads'));
const ErrorPage = lazy(() => import('./pages/Error'));
const LicenseIndexPage = lazy(() => import('./pages/license/index'));
const LicenseThankYouPage = lazy(() => import('./pages/license/thankyou'));
const LicenseTrialStartPage = lazy(() => import('./pages/license/trial/start'));
const LicenseRequestPage = lazy(() => import('./pages/licenseRequest'));
const LoginPage = lazy(() => import('./pages/login'));
const MyPlanIndexPage = lazy(() => import('./pages/myplan/index'));
const MyPlanCancelPage = lazy(() => import('./pages/myplan/cancel'));
const MyPlanModifyIndexPage = lazy(() => import('./pages/myplan/modify/index'));
const MyPlanModifyReviewPage = lazy(
  () => import('./pages/myplan/modify/review')
);
const MyPlanModifyThankYouPage = lazy(
  () => import('./pages/myplan/modify/thankyou')
);
const MyPlanBillingDetailsPage = lazy(
  () => import('./pages/myplan/billingdetails')
);
const MyPlanPaymentDetailsPage = lazy(
  () => import('./pages/myplan/paymentdetails')
);
const OrganizationPage = lazy(() => import('./pages/organization'));
const PartnerPage = lazy(() => import('./pages/partner'));
const PaymentDetailsPage = lazy(() => import('./pages/paymentdetails'));
const PersonalPage = lazy(() => import('./pages/personal'));
const ProfessionalPage = lazy(() => import('./pages/professional'));
const RedirectPage = lazy(() => import('./pages/redirect'));
const ReviewOrderPage = lazy(() => import('./pages/revieworder'));
const TaxExemptStartPage = lazy(() => import('./pages/taxexempt/start'));
const TaxExemptThankYouPage = lazy(() => import('./pages/taxexempt/thankyou'));
const ThankYouPage = lazy(() => import('./pages/thankyou'));
const TrialStartPage = lazy(() => import('./pages/trial/start'));
const TrialLicensePage = lazy(() => import('./pages/trialLicense'));

function RouteChangeTracker({ appInsights }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    setNavigator(navigate);
  }, [navigate]);
  useEffect(() => {
    if (appInsights?.trackPageView) {
      appInsights.trackPageView();
    }
  }, [location.pathname, appInsights]);
  return null;
}

function LayoutRoute() {
  const location = useLocation();
  const isLoginRoute =
    location.pathname === '/login' || location.pathname === '/login/';

  if (isLoginRoute) {
    return <Outlet />;
  }

  return (
    <ErrorBoundary>
      <Providers>
        <Layout>
          <Outlet />
        </Layout>
      </Providers>
    </ErrorBoundary>
  );
}

export default function App({ appInsights }) {
  return (
    <BrowserRouter>
        <LocaleProvider>
          <AuthProvider>
            <RouteChangeTracker appInsights={appInsights} />
            <Suspense fallback={null}>
              <Routes>
                <Route element={<LayoutRoute />}>
                  <Route path="/" element={<IndexPage />} />
                  <Route path="/account" element={<AccountPage />} />
                  <Route
                    path="/boardmakerLicense"
                    element={<BoardmakerLicensePage />}
                  />
                  <Route path="/community" element={<CommunityIndexPage />} />
                  <Route
                    path="/community/start"
                    element={<CommunityStartPage />}
                  />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/downloads" element={<DownloadsPage />} />
                  <Route path="/Error" element={<ErrorPage />} />
                  <Route path="/license" element={<LicenseIndexPage />} />
                  <Route
                    path="/license/thankyou"
                    element={<LicenseThankYouPage />}
                  />
                  <Route
                    path="/license/trial/start"
                    element={<LicenseTrialStartPage />}
                  />
                  <Route
                    path="/licenseRequest"
                    element={<LicenseRequestPage />}
                  />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/myplan" element={<MyPlanIndexPage />} />
                  <Route path="/myplan/cancel" element={<MyPlanCancelPage />} />
                  <Route
                    path="/myplan/modify"
                    element={<MyPlanModifyIndexPage />}
                  />
                  <Route
                    path="/myplan/modify/review"
                    element={<MyPlanModifyReviewPage />}
                  />
                  <Route
                    path="/myplan/modify/thankyou"
                    element={<MyPlanModifyThankYouPage />}
                  />
                  <Route
                    path="/myplan/billingdetails"
                    element={<MyPlanBillingDetailsPage />}
                  />
                  <Route
                    path="/myplan/paymentdetails"
                    element={<MyPlanPaymentDetailsPage />}
                  />
                  <Route path="/organization" element={<OrganizationPage />} />
                  <Route path="/partner" element={<PartnerPage />} />
                  <Route
                    path="/paymentdetails"
                    element={<PaymentDetailsPage />}
                  />
                  <Route path="/personal" element={<PersonalPage />} />
                  <Route path="/professional" element={<ProfessionalPage />} />
                  <Route path="/redirect" element={<RedirectPage />} />
                  <Route path="/revieworder" element={<ReviewOrderPage />} />
                  <Route
                    path="/taxexempt/start"
                    element={<TaxExemptStartPage />}
                  />
                  <Route
                    path="/taxexempt/thankyou"
                    element={<TaxExemptThankYouPage />}
                  />
                  <Route path="/thankyou" element={<ThankYouPage />} />
                  <Route path="/trial/start" element={<TrialStartPage />} />
                  <Route path="/trialLicense" element={<TrialLicensePage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthProvider>
        </LocaleProvider>
    </BrowserRouter>
  );
}
